import { useState } from "react";
// Link, НЕ NavLink: NavLink сам вирішує «активність» префіксним матчем і ставить
// aria-current/`active`-клас ПІСЛЯ {...rest} — перекрити пропом не можна, тож на
// /knowledge/admin обидва пункти («База знань» + «Розділи бази знань») отримали б
// aria-current="page". Джерело істини одне — navActiveRoute (AC-W11-ACTIVE-ROUTE).
import { Link, useLocation } from "react-router-dom";
import { Lock, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { useNavBadges } from "@/hooks/useNavBadges";
import { buildNavModel, navActiveRoute, type NavModelItem } from "@/lib/navRegistry";
import { cn } from "@/lib/cn";
import type { NavBadgeScope, NavBadgeStyle } from "@/api/types";

// NAV-1 (D-098/D-099): the sidebar renders EXCLUSIVELY from the backend
// `allowedRoutes` contract (/me) merged with the frontend-canonical
// lib/navRegistry — the old frontend-injected items (Зустрічі / Запити /
// Налаштування) and the visibleNavigation merge are gone. Zero role-inference:
// visibility, labels (navLabelKey), badge source/scope/style all backend-owned.

const COLLAPSED_LS_KEY = "kormax.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_LS_KEY) === "1";
  } catch {
    return false;
  }
}

/** Solid action pill / outline risk badge (AC-NAV-BADGE, AC-NAV-DIRECTOR-BADGE).
 *  Count is rendered as text (never color-only, §20); 0/undefined → not rendered.
 *  Exported so the M1 mobile drawer renders the IDENTICAL badge (truth parity). */
export function NavBadge({
  count,
  style,
  scope,
}: {
  count: number;
  style?: NavBadgeStyle;
  scope?: NavBadgeScope;
}) {
  if (style === "risk") {
    return (
      <span className="ml-auto inline-flex h-[17px] min-w-[18px] items-center justify-center gap-1 rounded-full border border-border px-1.5 text-[10.5px] font-semibold text-muted-foreground">
        <span aria-hidden className="h-[5px] w-[5px] shrink-0 rounded-full bg-info-foreground" />
        {count}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "ml-auto inline-flex h-[17px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold",
        scope === "team"
          ? "bg-info-foreground text-white dark:text-background"
          : "bg-primary-dark text-white dark:text-primary-foreground",
      )}
    >
      {count}
    </span>
  );
}

export function navItemAriaLabel(item: NavModelItem, count: number | undefined): string {
  let label = item.label;
  if (count) label += `, ${count}`;
  if (item.readOnly) label += " (лише перегляд)";
  return label;
}

export function Sidebar() {
  const allowedRoutes = useAuthStore((s) => s.allowedRoutes);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { badgeCount } = useNavBadges(allowedRoutes);
  const sections = buildNavModel(allowedRoutes);
  // AC-W11-ACTIVE-ROUTE: exactly ONE item is active — the longest matching nav
  // route (NavLink's own prefix isActive would light up /knowledge AND
  // /knowledge/admin). Shared verbatim with MobileNavDrawer (truth parity).
  const activeRoute = navActiveRoute(sections, useLocation().pathname);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSED_LS_KEY, prev ? "0" : "1");
      } catch {
        /* storage unavailable — keep in-memory state */
      }
      return !prev;
    });
  };

  return (
    <aside
      className={cn(
        "hidden md:flex shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground transition-[width]",
        collapsed ? "w-14" : "w-60",
      )}
    >
      <div className={cn("h-14 flex items-center gap-2 border-b", collapsed ? "justify-center px-0" : "px-5")}>
        <div className="w-7 h-7 shrink-0 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
          K
        </div>
        {!collapsed && (
          <span className="font-semibold tracking-tight">
            KORMAX <span className="text-muted-foreground">CRM</span>
          </span>
        )}
      </div>

      <nav
        role="navigation"
        aria-label="Основна навігація"
        className={cn("flex-1 overflow-y-auto py-3", collapsed ? "px-2 space-y-3" : "px-2 space-y-5")}
      >
        {sections.map(({ section, items }, sectionIdx) => (
          <div key={section}>
            {collapsed ? (
              sectionIdx > 0 && <div aria-hidden className="mx-1 mb-2 border-t border-border" />
            ) : (
              <div className="px-2 mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {section}
              </div>
            )}
            <div className="space-y-0.5">
              {items.map((item) => {
                const Icon = item.icon;
                const count = badgeCount(item.badgeBinding);
                const isActive = item.route === activeRoute;
                return (
                  <Link
                    key={item.routeKey}
                    to={item.to}
                    aria-label={navItemAriaLabel(item, count)}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      // 13.5px + px-2.5 = mockup metrics so the longest locked labels
                      // («Клієнти / Контрагенти», «Профілі на доповнення» + badge)
                      // fit the 240px rail without ellipsis (BuildNotes P4).
                      "group relative w-full flex items-center rounded-lg text-[13.5px] transition-colors min-h-[var(--density-input-h)] py-2",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      collapsed ? "justify-center px-0" : "gap-2.5 px-2.5",
                      isActive
                        ? "bg-primary-soft text-primary-dark font-semibold"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    <>
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute -left-2 top-2 bottom-2 w-[3px] rounded-r bg-primary"
                          />
                        )}
                        <span className="relative shrink-0">
                          <Icon
                            className={cn("w-[18px] h-[18px]", isActive && "text-primary")}
                            strokeWidth={2}
                          />
                          {collapsed && count !== undefined && (
                            <span
                              aria-hidden
                              className={cn(
                                "absolute -top-1 -right-1 h-[7px] w-[7px] rounded-full border border-sidebar",
                                item.badgeStyle === "risk" ? "bg-info-foreground" : "bg-primary",
                              )}
                            />
                          )}
                        </span>
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate text-left">{item.label}</span>
                            {item.placeholderNote && (
                              <span className="ml-1 shrink-0 rounded border border-border bg-background px-1 text-[9.5px] font-semibold text-muted-foreground">
                                {item.placeholderNote}
                              </span>
                            )}
                            {item.readOnly && (
                              <Lock
                                aria-hidden
                                className="ml-1 h-[13px] w-[13px] shrink-0 text-warning-foreground"
                              />
                            )}
                            {count !== undefined && (
                              <NavBadge count={count} style={item.badgeStyle} scope={item.badgeScope} />
                            )}
                          </>
                        )}
                        {collapsed && (
                          // Tooltip = label + count; keyboard-accessible (focus-visible),
                          // content duplicated for SR via the link's aria-label.
                          <span
                            aria-hidden
                            className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                          >
                            {item.label}
                            {count !== undefined ? ` · ${count}` : ""}
                          </span>
                        )}
                    </>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={cn("border-t py-2", collapsed ? "px-2" : "px-3")}>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Розгорнути меню" : "Згорнути меню"}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-lg py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            collapsed ? "justify-center px-0" : "px-3",
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={2} />
          ) : (
            <>
              <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={2} />
              <span>Згорнути</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
